import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import type { ChangeEvent, MouseEvent, MouseEventHandler, ReactElement } from 'react';

import Icon, { registerIcon } from '../Icon';
import * as caretDownIcon from '../Icon/icons/caret-down.icon';
import { useEscape, useFocusWithin, useI18n, useTheme } from '../../hooks';
import { createStringMatcher, isValidElement } from '../../utils';
import { isSolidColor } from '../../styles';
import { ThemeOverride } from '../Configuration';
import Popover from '../Popover';
import Menu from '../Menu';
import type { MenuItemProps } from '../Menu';

import type { AppTopNavProps } from './AppTopNav.types';
import type { LinkProps, OperatorProps, UtilItemProps } from './AppShell.types';
import {
  StyledTopNav,
  StyledTopNavList,
  StyledTopNavMeasureRow,
  StyledTopNavSection,
  StyledTopNavUtilBar,
  StyledTopNavItem,
  StyledTopNavItemHidden,
  StyledTopNavUtilButton,
  StyledTopNavCountBadge,
  StyledTopNavCreateArea
} from './AppTopNav.styles';
import { navContrastColors } from './style-utils';
import { CaseTypes } from './AppNavigationPanel';
import TopNavMoreMenu from './TopNavMoreMenu';
import { useTopNavOverflow } from './useTopNavOverflow';
import { AppShellDrawerContext } from './AppShellContext';

registerIcon(caretDownIcon);

const hasActiveDescendant = (link: LinkProps): boolean => {
  if (link.active) return true;
  return link.links?.some(hasActiveDescendant) ?? false;
};

const linkToFlyoutItem = (link: LinkProps): MenuItemProps => {
  const userOnClick = link.onClick;
  const children = link.links?.map(linkToFlyoutItem);
  const hasChildren = children && children.length > 0;
  return {
    id: link.id || link.name,
    primary: link.name,
    visual: link.icon ? <Icon name={link.icon} /> : undefined,
    href: hasChildren ? undefined : link.href,
    // MenuItem uses `typeof selected !== 'boolean'` to decide expand vs click.
    selected: hasChildren ? undefined : link.active || undefined,
    onClick: userOnClick
      ? (_id, e) => {
          userOnClick(e);
        }
      : undefined,
    items: hasChildren ? children : undefined
  };
};

const TopNavMeasureItem = ({ link }: { link: LinkProps }) => {
  const hasChildren = !!link.links && link.links.length > 0;
  return (
    <StyledTopNavItem as='span' tabIndex={-1}>
      {link.icon && <Icon name={link.icon} />}
      <span>{link.name}</span>
      {hasChildren && <Icon name='caret-down' />}
    </StyledTopNavItem>
  );
};

const TopNavLink = ({ link }: { link: LinkProps }) => {
  const hasChildren = !!link.links && link.links.length > 0;
  const active = hasActiveDescendant(link);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const popoverRef = useRef<HTMLDivElement>(null);
  const [popoverOpen, setPopoverOpen] = useState(false);
  const [filterValue, setFilterValue] = useState('');

  const close = useCallback(() => setPopoverOpen(false), []);

  useFocusWithin<HTMLElement>(
    [triggerRef, popoverRef],
    useCallback((isFocused: boolean) => {
      if (!isFocused) setPopoverOpen(false);
    }, [])
  );

  useEscape(
    useCallback(
      (e: KeyboardEvent) => {
        if (popoverOpen) {
          setPopoverOpen(false);
          e.stopPropagation();
          triggerRef.current?.focus();
        }
      },
      [popoverOpen]
    )
    // No target: attach to document so Escape works when focus is inside the
    // Popover portal (events don't bubble to the trigger element across portals).
  );

  if (hasChildren && link.links) {
    const allItems = link.links.map(linkToFlyoutItem);
    const useFilter = allItems.length >= 5;
    const filterRegex = createStringMatcher(filterValue, 'contains');
    const items = useFilter
      ? allItems.filter(it => filterRegex.test(String(it.primary ?? '')))
      : allItems;

    const handleTriggerClick = () => {
      if (!popoverOpen) setFilterValue('');
      setPopoverOpen(v => !v);
    };

    return (
      <>
        <StyledTopNavItem
          ref={triggerRef}
          $active={active}
          aria-haspopup='menu'
          aria-expanded={popoverOpen}
          onClick={handleTriggerClick}
        >
          {link.icon && <Icon name={link.icon} />}
          <span>{link.name}</span>
          <Icon name='caret-down' />
        </StyledTopNavItem>
        {popoverOpen && (
          <Popover ref={popoverRef} target={triggerRef.current} placement='bottom-start'>
            <Menu
              mode='action'
              variant='flyout'
              aria-label={link.name}
              items={items}
              onItemClick={close}
              focusControlEl={triggerRef.current ?? undefined}
              {...(useFilter
                ? {
                    filterInputProps: {
                      value: filterValue,
                      onChange: (e: ChangeEvent<HTMLInputElement>) => setFilterValue(e.target.value)
                    }
                  }
                : {})}
            />
          </Popover>
        )}
      </>
    );
  }

  const handleClick: MouseEventHandler<HTMLElement> = e => {
    link.onClick?.(e);
  };

  return (
    <StyledTopNavItem
      href={link.href}
      onClick={handleClick}
      $active={active}
      aria-current={link.active ? 'page' : undefined}
    >
      {link.icon && <Icon name={link.icon} />}
      <span>{link.name}</span>
    </StyledTopNavItem>
  );
};

const TopNavUtilItem = ({
  item,
  onUtilItemClick
}: {
  item: UtilItemProps;
  onUtilItemClick?: (item: UtilItemProps) => void;
}) => {
  const hasMenu = !!item.actions && item.actions.length > 0;
  const triggerRef = useRef<HTMLButtonElement>(null);
  const popoverRef = useRef<HTMLDivElement>(null);
  const [popoverOpen, setPopoverOpen] = useState(false);

  const close = useCallback(() => setPopoverOpen(false), []);

  useFocusWithin<HTMLElement>(
    [triggerRef, popoverRef],
    useCallback((isFocused: boolean) => {
      if (!isFocused) setPopoverOpen(false);
    }, [])
  );

  useEscape(
    useCallback(
      (e: KeyboardEvent) => {
        if (popoverOpen) {
          setPopoverOpen(false);
          e.stopPropagation();
          triggerRef.current?.focus();
        }
      },
      [popoverOpen]
    )
  );

  if (hasMenu && item.actions) {
    return (
      <>
        <StyledTopNavUtilButton
          ref={triggerRef}
          aria-label={item.count ? `${item.name}, ${String(item.count)}` : item.name}
          aria-haspopup='menu'
          aria-expanded={popoverOpen}
          onClick={() => setPopoverOpen(v => !v)}
        >
          {item.visual}
          {!!item.count && (
            <StyledTopNavCountBadge aria-hidden='true'>{item.count}</StyledTopNavCountBadge>
          )}
        </StyledTopNavUtilButton>
        {popoverOpen && (
          <Popover ref={popoverRef} target={triggerRef.current} placement='bottom-end'>
            <Menu
              mode='action'
              variant='flyout'
              aria-label={item.name}
              items={item.actions.map(
                (act: OperatorProps['actions'][number]): MenuItemProps => ({
                  id: act.id ?? act.primary,
                  primary: act.primary,
                  visual: act.visual,
                  href: act.href,
                  onClick: act.onClick
                    ? (_id, e) => {
                        act.onClick?.(e);
                        close();
                      }
                    : undefined
                })
              )}
              onItemClick={close}
              focusControlEl={triggerRef.current ?? undefined}
            />
          </Popover>
        )}
      </>
    );
  }

  return (
    <StyledTopNavUtilButton
      aria-label={item.count ? `${item.name}, ${String(item.count)}` : item.name}
      onClick={(e: MouseEvent<HTMLElement>) => {
        if (item.drawerView) {
          onUtilItemClick?.(item);
        }
        item.onClick?.(e);
      }}
    >
      {item.visual}
      {!!item.count && (
        <StyledTopNavCountBadge aria-hidden='true'>{item.count}</StyledTopNavCountBadge>
      )}
    </StyledTopNavUtilButton>
  );
};

const isUtilItemProps = (u: UtilItemProps | ReactElement): u is UtilItemProps => !isValidElement(u);

const AppTopNav = ({ caseTypes, links, cases, agent, utils, onUtilItemClick }: AppTopNavProps) => {
  const t = useI18n();
  const theme = useTheme();
  const { openDrawer, drawerOpen, openItemName, refreshDrawer } = useContext(AppShellDrawerContext);

  useEffect(() => {
    if (!drawerOpen || !openItemName || !utils) return;
    const freshItem = utils.filter(isUtilItemProps).find(u => u.name === openItemName);
    if (freshItem) refreshDrawer(freshItem);
  }, [utils, drawerOpen, openItemName, refreshDrawer]);

  const handleUtilItemClick = (item: UtilItemProps) => {
    if (onUtilItemClick) {
      onUtilItemClick(item);
      return;
    }
    if (item.drawerView) {
      openDrawer(item);
    }
  };

  const navTheme = useMemo(() => {
    const { foreground, interactive, urgent, backgroundColor } = navContrastColors(theme);
    const navBg = theme.components['app-shell'].nav.background;
    return {
      base: {
        palette: {
          'primary-background': isSolidColor(navBg) ? backgroundColor : 'transparent',
          'foreground-color': foreground,
          interactive,
          urgent
        }
      }
    };
  }, [theme]);

  const linkList = links ?? [];
  const moreButtonRef = useRef<HTMLButtonElement>(null);
  const { containerRef, measureRef, result } = useTopNavOverflow(linkList, { moreButtonRef });

  const visibleLinks = linkList.slice(0, result.visibleCount);
  const overflowLinks = linkList.slice(result.visibleCount);

  return (
    <ThemeOverride theme={navTheme}>
      <StyledTopNav aria-label={t('app_shell_main_navigation_label')} data-app-region>
        {caseTypes && caseTypes.length > 0 && (
          <StyledTopNavCreateArea container={{ alignItems: 'center' }} item={{ shrink: 0 }}>
            <CaseTypes caseTypes={caseTypes} />
          </StyledTopNavCreateArea>
        )}

        <StyledTopNavList
          ref={containerRef}
          container={{ alignItems: 'center', gap: 1 }}
          item={{ grow: 1 }}
        >
          <StyledTopNavMeasureRow
            ref={measureRef}
            container={{ alignItems: 'center', gap: 1 }}
            aria-hidden='true'
          >
            {linkList.map(link => (
              <TopNavMeasureItem key={link.id} link={link} />
            ))}
          </StyledTopNavMeasureRow>

          {visibleLinks.map(link => (
            <TopNavLink key={link.id} link={link} />
          ))}

          <StyledTopNavItemHidden
            ref={moreButtonRef}
            type='button'
            aria-hidden='true'
            tabIndex={-1}
          >
            <Icon name='bars' />
            <span>{t('app_shell_more')}</span>
          </StyledTopNavItemHidden>

          {overflowLinks.length > 0 && (
            <TopNavMoreMenu
              items={overflowLinks}
              label={t('app_shell_more')}
              active={overflowLinks.some(hasActiveDescendant)}
            />
          )}
        </StyledTopNavList>

        {cases && cases.length > 0 && (
          <StyledTopNavSection
            container={{ alignItems: 'center', gap: 1 }}
            item={{}}
            role='group'
            aria-label={t('active_cases')}
          >
            {cases.map(c => (
              <StyledTopNavItem
                key={c.id}
                onClick={c.onClick}
                $active={!!c.active}
                aria-current={c.active ? 'page' : undefined}
              >
                {c.visual ?? <Icon name='case-solid' />}
                <span>{c.primary}</span>
              </StyledTopNavItem>
            ))}
          </StyledTopNavSection>
        )}

        <StyledTopNavUtilBar container={{ alignItems: 'center', gap: 1 }} item={{ shrink: 0 }}>
          {agent && (
            <StyledTopNavUtilButton
              aria-label={agent.name}
              aria-current={agent.active ? 'page' : undefined}
              onClick={(e: MouseEvent<HTMLElement>) => {
                agent.onClick?.(e);
              }}
            >
              {agent.icon}
            </StyledTopNavUtilButton>
          )}
          {utils?.map((util, idx) => {
            if (isValidElement(util)) {
              const utilKey = util.key ?? `util-el-${String(idx)}`;
              return <span key={utilKey}>{util}</span>;
            }
            return (
              <TopNavUtilItem key={util.name} item={util} onUtilItemClick={handleUtilItemClick} />
            );
          })}
        </StyledTopNavUtilBar>
      </StyledTopNav>
    </ThemeOverride>
  );
};

export default AppTopNav;
